import classNames from 'classnames';
import React, { useState } from 'react';

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

type AccordionTitleClickArg = JsonValue | React.MouseEvent<HTMLSpanElement>;

type AccordionContentProps = {
  itemToHighlight?: number | null;
};

interface AccordionItemData {
  content: React.ComponentType<AccordionContentProps>;
  title: React.ReactNode;
  info?: React.ReactNode;
  value?: JsonValue;
  onTitleClick?: (valueOrEvent: AccordionTitleClickArg) => void;
}

interface AccordionItemProps extends AccordionItemData {
  id: number;
  activeAccordion: number | null;
  setActiveAccordion: React.Dispatch<React.SetStateAction<number | null>>;
  stickyClassName: string;
  isQuickLink?: boolean;
  itemToHighlight?: number | null;
}

const AccordionItem: React.FC<AccordionItemProps> = (props) => {
  const {
    content: Content,
    title,
    info,
    value,
    onTitleClick,
    id,
    activeAccordion,
    setActiveAccordion,
    stickyClassName,
    isQuickLink,
    ...rest
  } = props;

  const opened = activeAccordion === id;

  const handleClick: React.MouseEventHandler<HTMLDivElement> = () => {
    setActiveAccordion(opened ? null : id);
  };

  return (
    <div
      className={classNames(
        'accordion-item',
        { 'accordion-item--opened': opened },
        { 'accordion-item--sticky-opened custom-scroll-bar': stickyClassName && opened }
      )}
    >
      <div
        className={classNames(`accordion-item__line ${stickyClassName}`, {
          'bg-grey-5': opened,
          'accordion-item__line--quick-link': isQuickLink,
        })}
        onClick={handleClick}
      >
        <h4 className={classNames('accordion-item__title', { 'text-grey-80': opened })}>
          <span
            className="accordion-item__label text-bold"
            onClick={(event) => {
              if (onTitleClick) {
                event.stopPropagation();
                onTitleClick(value !== undefined ? value : event);
              }
            }}
          >
            {title}
          </span>
          {info ? (
            <>
              <i className="accordion-item__info_separator" />
              <span className="accordion-item__info"> {info}</span>
            </>
          ) : (
            ''
          )}
        </h4>
        <span className={classNames('accordion-item__icon', { 'accordion-item__icon--quick-link': isQuickLink })} />
      </div>
      <div className="accordion-item__inner">
        <div className="accordion-item__content">
          <Content {...rest} />
        </div>
      </div>
    </div>
  );
};

interface AccordionProps {
  data: AccordionItemData[];
  categoryToOpen?: number | null;
  itemToHighlight?: number | null;
  stickyClassName?: string;
  isQuickLink?: boolean;
  className?: string;
}

const Accordion: React.FC<AccordionProps> = ({
  data,
  categoryToOpen = null,
  itemToHighlight = null,
  stickyClassName = '',
  isQuickLink,
  className = '',
}) => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(categoryToOpen);

  return (
    <div className={classNames('accordion-container custom-scroll-bar', { [className]: className })}>
      <ul className="accordion-list">
        {data.map((obj, key) => (
          <li className="accordion-list__item" key={key}>
            <AccordionItem
              {...obj}
              id={key}
              activeAccordion={activeAccordion}
              setActiveAccordion={setActiveAccordion}
              stickyClassName={stickyClassName}
              isQuickLink={isQuickLink}
              itemToHighlight={itemToHighlight}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Accordion;
